// The book table's status chip, made answerable.
//
// A chip that says "Critical" and nothing else states a verdict without its
// reasoning. Clicking it opens the EVIDENCE — every alert feeding that row's
// status, exactly as the alerts panel shows them — plus where to go next:
// the kyck dashboards (what's wrong) AND the external ad accounts (where you
// fix it), which is what makes this a complete unit rather than a pointer to
// another read-only screen.
//
// ⚠️ IT LISTS EVERY ALERT TYPE. The panel's chips/platform toggle narrow the
// QUEUE; this popover is the row's ground truth, so it must show what the
// status was actually computed from — otherwise a filtered-away alert would
// make a Critical row look unexplained.
//
// An ON-TRACK row says so plainly instead of opening an empty box.

import { Link } from 'react-router-dom';
import { ExternalLink } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { cn } from '../../lib/utils';
import type { HomeAlert } from '../../lib/advertisingHome/alerts';
import type { HomeAccount, HomeAccountHealth } from '../../types/advertisingHome';
import { AlertTypeBadge, ChannelChip, SeverityDot } from './AlertBadges';
import {
  channelReportingHref,
  googleAdsHref,
  googleReportingHref,
  metaAdsManagerHref,
  metaReportingHref,
} from './deepLinks';
import { AccountLinkIcons } from '../../components/ui/AccountLinkIcons';
import type { AccountLinkKey } from '../../lib/accountLinks';

const HEALTH_LABEL: Record<HomeAccountHealth, { label: string; cls: string }> = {
  critical: { label: 'Critical', cls: 'bg-red-50 text-red-700 border-red-200' },
  attention: { label: 'Attention', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  ontrack: { label: 'On track', cls: 'bg-kyck-50 text-kyck-700 border-kyck-200' },
};

function LinkRow({
  to,
  external,
  children,
}: {
  to: string;
  external?: boolean;
  children: React.ReactNode;
}): React.JSX.Element {
  const cls =
    'flex items-center gap-1.5 rounded px-2 py-1 text-xs text-kyck-700 hover:bg-accent';
  if (external) {
    return (
      <a href={to} target="_blank" rel="noreferrer" className={cls}>
        {children}
        <ExternalLink className="h-3 w-3" aria-hidden />
      </a>
    );
  }
  return (
    <Link to={to} className={cls}>
      {children}
    </Link>
  );
}

export function StatusPopover({
  account,
  health,
  alerts,
  defaultOpen,
  showLinkIcons = false,
  linkKeys,
}: {
  account: HomeAccount;
  health: HomeAccountHealth;
  /** EVERY alert for this account — the status's own inputs, unfiltered. */
  alerts: HomeAlert[];
  /** Radix's own prop, passed through — lets tests assert the content. */
  defaultOpen?: boolean;
  /** Round 2 item 3 — the account's external link icons in the header (opt-in). */
  showLinkIcons?: boolean;
  /** Restrict the icon strip to these links. */
  linkKeys?: AccountLinkKey[];
}): React.JSX.Element {
  const chip = HEALTH_LABEL[health];
  const metaExternal = metaAdsManagerHref(account.fbAdAccountId);
  const googleExternal = googleAdsHref(account.googleAdsCustomerId);

  return (
    <Popover defaultOpen={defaultOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            'inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium transition-colors hover:brightness-95',
            chip.cls,
          )}
        >
          {chip.label}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-2">
        <div className="flex items-center gap-1 px-1 pb-1">
          <p className="min-w-0 truncate text-xs font-semibold">{account.name}</p>
          {showLinkIcons && <AccountLinkIcons accountId={account.id} className="shrink-0" only={linkKeys} />}
        </div>

        {alerts.length === 0 ? (
          // Plainly, rather than an empty box.
          <p className="px-1 pb-2 text-xs text-muted-foreground">
            On track — no alerts on either channel.
          </p>
        ) : (
          <ul className="mb-1 space-y-1.5 border-b border-border pb-2">
            {alerts.map((a) => (
              <li key={`${a.channel}-${a.type}`} className="px-1">
                <span className="flex flex-wrap items-center gap-1.5">
                  <SeverityDot severity={a.severity} />
                  <AlertTypeBadge type={a.type} />
                  <ChannelChip channel={a.channel} />
                </span>
                <span className="mt-0.5 block text-xs text-muted-foreground">{a.reason}</span>
                {/* The evidence, at the same weight the panel gives it. */}
                <span className="block text-xs font-medium tabular-nums">{a.figure}</span>
                <Link
                  to={channelReportingHref(a.channel, a.accountId)}
                  className="text-[11px] text-kyck-700 hover:underline"
                >
                  Open in {a.channel === 'meta' ? 'Meta' : 'Google'} reporting →
                </Link>
              </li>
            ))}
          </ul>
        )}

        <p className="px-2 pb-0.5 pt-1 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
          Analyse in kyck
        </p>
        <LinkRow to={metaReportingHref(account.id)}>Meta reporting</LinkRow>
        <LinkRow to={googleReportingHref(account.id)}>Google Ads reporting</LinkRow>

        {(metaExternal || googleExternal) && (
          <>
            <p className="px-2 pb-0.5 pt-1.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
              Fix in the ad account
            </p>
            {metaExternal && (
              <LinkRow to={metaExternal} external>
                Meta Ads Manager
              </LinkRow>
            )}
            {googleExternal && (
              <LinkRow to={googleExternal} external>
                Google Ads
              </LinkRow>
            )}
          </>
        )}
      </PopoverContent>
    </Popover>
  );
}
